<script>
import api from '@utils/api'
import { isNil, sortBy } from 'lodash'
import { createBarcodeScanner, isMobile } from '@utils/barcode/barcode-scanner'

export default {
  name: 'NewReturnsSpecification',
  props: {
    newSpecification: {
      type: Boolean,
      default: false,
    },
    currentDocument: {
      type: Object,
      default: () => {},
    },
  },
  data() {
    return {
      loading: false,
      valid: true,
      document: this.$route.params.document,
      item: {
        document: this.$route.params.document.id,
        goods: null,
        serial: null,
        unit: 104,
        quantity: 1,
      },
      rules: {
        goods: [x => !!x || this.$t('validation__is_required')],
        serial: [x => !!x || this.$t('validation__is_required')],
      },
      serialNumbersLoading: false,
      serialNumbers: [],
      goods: [],
      scannerIsVisible: false,
      barcodeScanner: createBarcodeScanner(document),
      isMobile: isMobile(),
    }
  },
  computed: {
    currentGoods() {
      return this.item.goods
    },
  },
  watch: {
    newSpecification(value) {
      if (value) this.getGoods()
    },
    currentGoods(value) {
      if (isNil(value)) return
      this.serialNumbersLoading = true
      this.item.serial = null
      api
        .getSerialNumbers(this.currentDocument.fromWarehouse, null, value)
        .then(response => {
          this.serialNumbers = sortBy(response)
          if (response.length === 1) this.item.serial = response[0]
          this.serialNumbersLoading = false
        })
        .catch(() => {})
        .then(() => {
          this.serialNumbersLoading = false
        })
    },
  },
  methods: {
    getValue(dictionaryName, value) {
      return this.$store.getters['data/getValueFromDictionary'](
        dictionaryName,
        value
      )
    },
    clearItem() {
      this.item.goods = null
      this.item.serial = null
      this.serialNumbers = []
      this.goods = []
      this.valid = true
    },
    submit() {
      let selectedArticle = this.item.goods
      if (this.$refs.newSpecificationForm.validate()) {
        this.loading = true
        api.createSpecification(this.item).then(response => {
          this.$refs.newSpecificationForm.reset()
          this.item.goods = selectedArticle
          this.$emit('spec-added')
          this.loading = false
        })
      }
    },
    closeModal() {
      if (this.serialNumbers.length) this.$refs.newSpecificationForm.reset()
      this.$emit('close-modal')
    },
    getGoods() {
      const allGoods = this.$store.getters['data/getGoods'](this.document.type)
      api
        .getQuantityGoods(this.currentDocument.fromWarehouse)
        .then(response => {
          const result = []
          for (const item of response.data) {
            const goods = allGoods.find(elem => elem.id === item.goodsId)
            if (goods) result.push(goods)
          }
          this.goods = result.map(item => {
            return {
              id: item.id,
              shortName: `[${item.articleNumber}] ${item.shortName}`,
            }
          })
          if (result.length === 1) this.item.goods = result[0].id
        })
    },
    openBarcodeScanner(field, trasformValueFn) {
      this.scannerIsVisible = true
      this.barcodeScanner.openScannerForField(
        value =>
          this.setFieldValue(
            field,
            trasformValueFn ? trasformValueFn(value) : value
          ),
        () => this.closeScanner()
      )
    },
    setFieldValue(field, value) {
      this.item[field] = value
    },
    findGoodsById(itemId) {
      let filteredById = this.goods.filter(
        goodsItem => goodsItem.id === parseInt(itemId)
      )
      if (filteredById && filteredById.length > 0) return filteredById[0]
      else
        this.$store.commit('notifications/ADD', {
          type: 'warning',
          message: this.$t('error__article_not_found'),
        })
    },
    closeScanner() {
      this.barcodeScanner.closeScanner()
      this.scannerIsVisible = false
    },
  },
}
</script>

<template>
  <v-dialog
    :value="newSpecification"
    :content-class="$style.relativePosition"
    persistent
    max-width="500px"
  >
    <v-card>
      <v-card-title>
        <v-spacer />
        <div class="headline">{{ $t('titles__new_specification') }}</div>
        <v-spacer />
      </v-card-title>
      <v-card-text>
        <v-container grid-list-md>
          <v-layout wrap>
            <v-flex xs12>
              <v-form
                ref="newSpecificationForm"
                v-model="valid"
                lazy-validation
              >
                <v-text-field
                  :label="$t('label__document')"
                  :value="document.number"
                  name="document"
                  readonly
                />
                <v-layout 
                  row 
                  align-center>
                  <v-flex>
                    <v-select
                      :items="goods"
                      :label="$t('label__article')"
                      v-model="item.goods"
                      :rules="rules.goods"
                      item-text="shortName"
                      item-value="id"
                      required
                      autocomplete
                    />
                  </v-flex>
                  <v-flex 
                    v-if="isMobile" 
                    shrink>
                    <img
                      class="barcodeScanIcon"
                      src="@assets/images/barcode_scan_icon.png"
                      @click="openBarcodeScanner('goods', findGoodsById)"
                    >
                  </v-flex>
                </v-layout>
                <v-layout 
                  row 
                  align-center>
                  <v-flex>
                    <v-select
                      :items="serialNumbers"
                      :label="$t('label__serial')"
                      v-model="item.serial"
                      :rules="rules.serial"
                      :disabled="!serialNumbers.length"
                      :loading="serialNumbersLoading"
                      required
                      autocomplete
                    />
                  </v-flex>
                  <v-flex 
                    v-if="isMobile"
                    shrink>
                    <img
                      :class="['barcodeScanIcon', !serialNumbers.length ? 'imageDisabled' : '' ]"
                      src="@assets/images/barcode_scan_icon.png"
                      @click="(event) => serialNumbers.length && openBarcodeScanner('serial')"
                    >
                  </v-flex>
                </v-layout>
                <v-text-field
                  :label="$t('label__unit')"
                  :value="getValue('unit', item.unit)"
                  name="uni"
                  readonly
                />
                <v-text-field
                  :label="$t('label__quantity')"
                  :value="item.quantity"
                  name="quantity"
                  readonly
                />
              </v-form>
              <small>* {{ $t('label__requier_small_text') }}</small>
            </v-flex>
          </v-layout>
        </v-container>
      </v-card-text>
      <v-card-actions>
        <v-spacer/>
        <v-btn
          :disabled="loading"
          color="secondary"
          @click="closeModal">{{ $t('buttons__close') }}</v-btn>
        <v-btn
          :disabled="loading"
          :loading="loading"
          color="primary"
          @click="submit">{{ $t('buttons__create') }}</v-btn>
      </v-card-actions>
    </v-card>
    <v-card
      v-show="scannerIsVisible"
      id="scanner-container"
      class="scannerContainer">
      <v-btn
        class="scannerCancelBtn"
        color="secondary"
        @click="closeScanner()"
      >
        {{ $t('buttons__cancel') }}
      </v-btn>
    </v-card>
    <div 
      v-if="scannerIsVisible"
      class="blurred"/>
  </v-dialog>
</template>

<style lang="scss" module>
@import '@design';
.warningMsg {
  color: #ff5252;
}
.relativePosition {
  position: relative;
}
</style>
